import { Carousel } from "@mantine/carousel";
import {
  AspectRatio,
  Badge,
  Box,
  Card,
  Group,
  Image,
  type MantineStyleProp,
  Modal,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBuildingStore, IconQuote, IconStarFilled } from "@tabler/icons-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { Reveal } from "$/components/reveal";
import { Section } from "$/components/section";
import { type Testimonial, testimonials } from "$/data/testimonials";

// Repete os depoimentos para garantir largura suficiente e um loop contínuo
// suave (embla precisa de slides além da largura do container para o loop).
const marqueeItems = [...testimonials, ...testimonials, ...testimonials];

// Sparkles cintilando em pontos espalhados sobre as estrelas. Posições e tempos
// são fixos (nada de Math.random no render → seguro para o SSR/hidratação); os
// delays/durações desencontrados dão a sensação de aleatoriedade.
type Sparkle = { left: string; top: string; size: number; delay: string; duration: string };
const SPARKLES: Sparkle[] = [
  { left: "6%", top: "18%", size: 16, delay: "0s", duration: "1.9s" },
  { left: "18%", top: "85%", size: 12, delay: "0.7s", duration: "2.3s" },
  { left: "30%", top: "0%", size: 18, delay: "1.4s", duration: "1.7s" },
  { left: "40%", top: "55%", size: 13, delay: "0.3s", duration: "2.1s" },
  { left: "52%", top: "20%", size: 17, delay: "1.1s", duration: "1.8s" },
  { left: "63%", top: "90%", size: 12, delay: "1.9s", duration: "2.4s" },
  { left: "74%", top: "8%", size: 15, delay: "0.5s", duration: "2s" },
  { left: "85%", top: "60%", size: 14, delay: "1.6s", duration: "1.6s" },
  { left: "96%", top: "25%", size: 13, delay: "0.9s", duration: "2.2s" },
];

export function Testimonials() {
  const [opened, { open, close }] = useDisclosure(false);
  const [active, setActive] = useState<Testimonial | null>(null);
  const lastStarRef = useRef<HTMLSpanElement>(null);

  // Animação ligada ao scroll: a 5ª estrela começa grande e centralizada no topo
  // da seção e, conforme se rola para dentro dela, volta encolhendo até o lugar
  // de origem (ao lado das outras). Tudo dentro do useEffect → seguro p/ o SSR.
  useEffect(() => {
    const star = lastStarRef.current;
    const slot = star?.parentElement;
    if (!star || !slot) return;

    // Respeita quem prefere menos movimento: mantém a estrela no lugar.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const BIG = 7; // escala máxima
    const DURATION = 1200; // ms — duração fixa e suave, independente do scroll
    let raf = 0;
    let started = false;
    let startT = 0;

    const measure = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const slotRect = slot.getBoundingClientRect();
      const slotCx = slotRect.left + slotRect.width / 2;
      // dx0/dy0 = deslocamento do estado "grande" (centralizado na tela e um
      // pouco acima da fileira) em relação à posição atual do slot.
      return { dx0: window.innerWidth / 2 - slotCx, dy0: -vh * 0.12 };
    };

    // Estado inicial: grande (scale 1 = tamanho nativo de 154px) e centralizada,
    // acompanhando a fileira até disparar.
    const setBig = () => {
      const { dx0, dy0 } = measure();
      star.style.transform = `translate(-50%, -50%) translate(${dx0}px, ${dy0}px) scale(1)`;
    };

    // Tween por TEMPO (não por posição de scroll): a animação é sempre visível e
    // suave, inclusive no clique do header que rola rápido até a seção.
    const frame = (now: number) => {
      if (!startT) startT = now;
      const tp = Math.min(1, (now - startT) / DURATION);
      const { dx0, dy0 } = measure();

      // Easings diferentes por eixo → caminho curvo em "C invertido": desce na
      // vertical cedo (ease-out) e só desliza na horizontal no fim (ease-in).
      const easeX = tp ** 3;
      const easeY = 1 - (1 - tp) ** 3;
      const easeS = 1 - (1 - tp) ** 3;
      const tx = dx0 * (1 - easeX);
      const ty = dy0 * (1 - easeY);
      // visual vai de BIG (nativo, scale 1) até 1 (encaixada); como a estrela é
      // renderizada em 154px, scale = visual / BIG nunca passa de 1 → sempre nítida.
      const visual = 1 + (BIG - 1) * (1 - easeS);
      const scale = visual / BIG;
      star.style.transform = `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`;

      if (tp < 1) {
        raf = requestAnimationFrame(frame);
      } else {
        star.style.transform = ""; // trava na origem (volta ao default do CSS = encaixada)
      }
    };

    setBig();

    // Dispara uma única vez assim que o slot entra na tela (borda inferior).
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !started) {
        started = true;
        io.disconnect();
        raf = requestAnimationFrame(frame);
      }
    });
    io.observe(slot);

    // Enquanto não disparou, mantém a estrela grande acompanhando a fileira.
    const onScroll = () => {
      if (!started) setBig();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Movimento contínuo (estilo marquee) que pausa ao passar o mouse e retoma
  // depois de arrastar — mantendo o carrossel arrastável com o mouse/swipe.
  const autoScroll = useRef(
    AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false, stopOnMouseEnter: true }),
  );

  const handleOpen = (t: Testimonial) => {
    setActive(t);
    open();
  };

  return (
    <Section id="testimonials" bg="gray.0">
      <Stack gap="sm" maw={680} mb={40}>
        <Box className="rating-stars" mb={4} aria-label="Avaliação cinco estrelas">
          <Group gap={4}>
            {Array.from({ length: 4 }).map((_, i) => (
              <IconStarFilled
                // biome-ignore lint/suspicious/noArrayIndexKey: estrelas decorativas e estáticas
                key={i}
                className="rating-star"
                size={22}
              />
            ))}
            {/* O slot fica fixo no layout (sem transform); o transform de scroll é
                aplicado no <span> interno, então as outras estrelas não se mexem. */}
            <span className="last-star-slot">
              {/* Renderizada grande (154 = 22 * BIG) e reduzida via scale → nítida. */}
              <span ref={lastStarRef} className="last-star">
                <IconStarFilled className="rating-star" size={154} />
              </span>
            </span>
          </Group>
          {SPARKLES.map((s, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: sparkles decorativos e estáticos
              key={i}
              className="sparkle"
              aria-hidden
              style={
                {
                  left: s.left,
                  top: s.top,
                  width: s.size,
                  height: s.size,
                  animationDelay: s.delay,
                  animationDuration: s.duration,
                } as CSSProperties
              }
            />
          ))}
        </Box>
        <Reveal>
          <Title
            order={2}
            fz={{ base: 32, md: 48 }}
            fw={800}
            lh={1.08}
            style={{ letterSpacing: "-0.03em" }}
          >
            Quem já usa o Verdana.
          </Title>
        </Reveal>
        <Reveal delay={100}>
          <Text c="gray.7" fz={{ base: 16, md: 18 }} lh={1.55}>
            Estabelecimentos de Garanhuns, PE, contando como é a rotina com o Verdana no dia a dia.
          </Text>
        </Reveal>
      </Stack>

      <Reveal delay={150}>
        <Carousel
          slideSize="auto"
          slideGap="md"
          withControls={false}
          emblaOptions={{ loop: true, dragFree: true, align: "start" }}
          plugins={[autoScroll.current]}
          styles={{
            // Respiro vertical para o lift + sombra do hover não serem cortados
            // pelo overflow:hidden do viewport (o clip horizontal continua valendo).
            viewport: { paddingTop: 12, paddingBottom: 28 },
          }}
          style={{
            cursor: "grab",
            maskImage:
              "linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, #000 5%, #000 95%, transparent 100%)",
          }}
        >
          {marqueeItems.map((t, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: itens repetidos de propósito para o loop contínuo
            <Carousel.Slide key={`${t.id}-${i}`} style={{ overflow: "visible" }}>
              <TestimonialCard data={t} onClick={() => handleOpen(t)} />
            </Carousel.Slide>
          ))}
        </Carousel>
      </Reveal>

      <TestimonialModal opened={opened} onClose={close} data={active} />
    </Section>
  );
}

type TestimonialCardProps = {
  data: Testimonial;
  onClick: () => void;
};

function TestimonialCard({ data, onClick }: TestimonialCardProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-label={`Ver depoimento de ${data.owner} - ${data.business}`}
      style={{ display: "block" }}
    >
      <Card
        padding="md"
        radius="lg"
        withBorder
        className="testimonial-card"
        w={280}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Badge
          variant="light"
          color="green"
          size="sm"
          radius="sm"
          mb={8}
          style={{ alignSelf: "flex-start" }}
        >
          {data.segment}
        </Badge>

        <AspectRatio ratio={4 / 3} mb="sm">
          <TestimonialImage data={data} />
        </AspectRatio>

        <Text c="gray.8" fz="sm" lh={1.5} fs="italic" lineClamp={3} mb="sm">
          “{data.quote}”
        </Text>

        <Stack gap={2} mt="auto">
          <Text fw={700} c="dark.9" fz="sm" lh={1.2}>
            {data.owner}
          </Text>
          <Text c="gray.6" fz="xs" lh={1.2}>
            {data.business}
          </Text>
        </Stack>
      </Card>
    </UnstyledButton>
  );
}

function TestimonialImage({ data, style }: { data: Testimonial; style?: MantineStyleProp }) {
  if (data.image) {
    return <Image src={data.image} alt={data.imageAlt} radius="md" style={style} />;
  }
  return (
    <Box
      bg="gray.2"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--mantine-color-gray-5)",
      }}
      aria-label={data.imageAlt}
    >
      <IconBuildingStore size={40} stroke={1.4} />
    </Box>
  );
}

type TestimonialModalProps = {
  opened: boolean;
  onClose: () => void;
  data: Testimonial | null;
};

function TestimonialModal({ opened, onClose, data }: TestimonialModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="lg"
      padding={0}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.6, blur: 3 }}
    >
      {data && (
        <Stack gap={0}>
          <Box p={{ base: "lg", sm: "xl" }}>
            <Badge
              variant="light"
              color="green"
              size="md"
              radius="sm"
              mb="md"
              style={{ alignSelf: "flex-start" }}
            >
              {data.segment}
            </Badge>

            <AspectRatio ratio={16 / 9} mb="lg">
              <TestimonialImage
                data={data}
                style={{ objectFit: data.objectFit, objectPosition: data.objectPosition }}
              />
            </AspectRatio>

            <IconQuote
              size={28}
              stroke={1.6}
              color="var(--mantine-color-green-6)"
              style={{ marginBottom: 8 }}
            />

            <Text c="dark.8" fz={{ base: 16, md: 18 }} lh={1.6} fs="italic" mb="lg">
              “{data.quote}”
            </Text>

            <Stack gap={2}>
              <Text fw={700} c="dark.9" fz="lg" lh={1.2}>
                {data.owner}
              </Text>
              <Text c="gray.6" fz="sm" lh={1.2}>
                {data.business}
              </Text>
            </Stack>
          </Box>
        </Stack>
      )}
    </Modal>
  );
}
